import { useCallback, useEffect, useMemo } from "react";

import { useGetCountries } from "@/api/country/useCountryAPI";
import {
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import ModalHeader from "@/components/ui/modal/modalHeader";
import {
  DataSettingPopupType,
  DataSettings,
  EDataSettingPointType,
  EDataSettingPopupType,
  EDataSettingSpatialType
} from "@/types/dataSetting";

import { Button } from "equator-design-system";

import MultipleGeometrySetting from "./MultipleGeometrySetting";
import NoGeometrySetting from "./NoGeometrySetting";
import SingleGeometrySetting from "./SingleGeometrySetting";

const TEST_ID_PREFIX = "dataset-management-data-setting-popup";

type DataSettingPopupProps = {
  countryId?: string;
  type: DataSettingPopupType;
  dataSetting: DataSettings | null;
  attributesLatitude: IOptionItem[];
  attributesLongitude: IOptionItem[];
  attributesForAdminArea: IOptionItem[];
  /** Rendered as data-tourid on the dialog so an onboarding tour can spotlight it. */
  tourId?: string;
  /** Rendered as data-tourid on the geometry-setting section for tour targeting. */
  spatialAggregationTourId?: string;
  /** Optional preloaded geometry attributes for onboarding or other fixed data. */
  geometryAttributeOptions?: IOptionItem[];
  disableAnimation?: boolean;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  onClose: () => void;
  onSaveDataSetting: () => void;
};

const DataSettingPopup = ({
  countryId,
  type,
  dataSetting,
  attributesForAdminArea,
  attributesLatitude,
  attributesLongitude,
  tourId,
  spatialAggregationTourId,
  geometryAttributeOptions,
  disableAnimation = false,
  setDataSetting,
  onClose,
  onSaveDataSetting
}: DataSettingPopupProps) => {
  const { data: countries, isLoading: isGettingCountries } = useGetCountries();

  const handleSelect = useCallback(
    <K extends keyof DataSettings>(key: K, value: DataSettings[K]) => {
      setDataSetting((prevDataSetting: DataSettings | null) => {
        if (prevDataSetting === null) {
          return { [key]: value };
        }
        return {
          ...prevDataSetting,
          [key]: value
        };
      });
    },
    [setDataSetting]
  );

  const handleSpatialAggregationSelect = <
    K extends keyof NonNullable<DataSettings["spatialAggregation"]>
  >(
    key: K,
    value: NonNullable<DataSettings["spatialAggregation"]>[K]
  ) => {
    setDataSetting((prevDataSetting: DataSettings | null) => {
      if (prevDataSetting === null) {
        return { spatialAggregation: { [key]: value } };
      }
      return {
        ...prevDataSetting,
        spatialAggregation: {
          ...prevDataSetting.spatialAggregation,
          [key]: value
        }
      };
    });
  };

  const isNoneTypeSaveDisabled = (dataSetting: DataSettings): boolean => {
    if (!dataSetting.spatialAggregation?.type?.value) return true;

    const spatialType = dataSetting.spatialAggregation.type.value;

    if (spatialType === EDataSettingSpatialType.Admin_Area)
      return !dataSetting.spatialAggregation.adminAreaAttribute;

    if (spatialType === EDataSettingSpatialType.Point) {
      if (!dataSetting.spatialAggregation.point?.pointType?.value) return true;

      const pointType = dataSetting.spatialAggregation.point.pointType.value;

      if (pointType === EDataSettingPointType.Coordinate)
        return (
          !dataSetting.spatialAggregation.point.pointCoordinate?.latAttribute ||
          !dataSetting.spatialAggregation.point.pointCoordinate?.longAttribute
        );

      if (pointType === EDataSettingPointType.Geocoding)
        return !dataSetting.spatialAggregation.point.geocodingAtrtribute;
    }

    return false;
  };

  const isMultipleTypeSaveDisabled = (dataSetting: DataSettings): boolean => {
    if (!dataSetting.spatialAggregation?.type?.value) return true;

    const multipleSpatialType = dataSetting.spatialAggregation.type.value;

    if (multipleSpatialType === EDataSettingSpatialType.Point)
      return !dataSetting.spatialAggregation.pointAttribute;

    if (multipleSpatialType === EDataSettingSpatialType.Polygon)
      return !dataSetting.spatialAggregation.polygonAttribute;

    return false;
  };

  const isSaveDisabled = (): boolean => {
    if (!dataSetting?.country) {
      return true;
    }
    switch (type) {
      case EDataSettingPopupType.None:
        return isNoneTypeSaveDisabled(dataSetting);
      case EDataSettingPopupType.Single:
        return false;
      case EDataSettingPopupType.Multiple:
        return isMultipleTypeSaveDisabled(dataSetting);
      default:
        return true;
    }
  };

  // Map countries data to option select
  const countriesMapping: IOptionItem[] = useMemo(() => {
    return (
      countries?.map((item) => ({
        label: item.name,
        value: item.id
      })) ?? []
    );
  }, [countries]);

  // To set default country value by dataset meta data
  useEffect(() => {
    if (!countryId || !countriesMapping?.length) return;
    const country = countriesMapping.find((item) => item.value === countryId);
    if (country) handleSelect("country", country);
  }, [countryId, countriesMapping, handleSelect]);

  return (
    <DialogContent
      isHideClose
      className="!rounded-xl w-[450px]"
      data-tourid={tourId}
      disableAnimation={disableAnimation}
    >
      <DialogTitle className="hidden" />
      <DialogDescription className="sr-only">
        Data Settings are used to configure the data for the dataset.
      </DialogDescription>
      <ModalHeader
        title="Data Settings"
        className="p-0 m-0"
        onClose={onClose}
      />
      <div className="flex flex-col">
        <Dropdown
          isLoading={isGettingCountries}
          placeholder="Country*"
          data={countriesMapping}
          value={dataSetting?.country || null}
          onSelect={(data) => {
            if (!data) return;
            handleSelect("country", data);
          }}
        />
        <div data-tourid={spatialAggregationTourId}>
          {type === EDataSettingPopupType.None && (
            <NoGeometrySetting
              attributesForAdminArea={attributesForAdminArea}
              attributesLatitude={attributesLatitude}
              attributesLongitude={attributesLongitude}
              dataSetting={dataSetting}
              handleSpatialAggregationSelect={handleSpatialAggregationSelect}
            />
          )}
          {type === EDataSettingPopupType.Single && (
            <SingleGeometrySetting
              dataSetting={dataSetting}
              handleSpatialAggregationSelect={handleSpatialAggregationSelect}
            />
          )}
          {type === EDataSettingPopupType.Multiple && (
            <MultipleGeometrySetting
              dataSetting={dataSetting}
              geometryAttributeOptions={geometryAttributeOptions}
              handleSpatialAggregationSelect={handleSpatialAggregationSelect}
            />
          )}
        </div>
      </div>
      <div className="flex gap-2 w-full rounded-t-none rounded-lg pb-4">
        <Button
          className="h-[40px] w-full btn-primary text-sm !font-bold"
          type="submit"
          disabled={isSaveDisabled()}
          onClick={onSaveDataSetting}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          <span>Save</span>
        </Button>
      </div>
    </DialogContent>
  );
};

export default DataSettingPopup;
